import { useState } from "react";
import { submitInquiry } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const FIELD_DEFS = {
  name: { label: "Name", type: "text", required: true },
  company: { label: "Company Name", type: "text" },
  city: { label: "City", type: "text" },
  state: { label: "State", type: "text" },
  phone: { label: "Mobile Number", type: "tel", required: true },
  email: { label: "Email Address", type: "email", required: true },
  current_brands: { label: "Current Brands Handled", type: "text" },
  experience_years: { label: "Years of Experience", type: "text" },
  subject: { label: "Subject", type: "text" },
  message: { label: "Message", type: "textarea" },
};

export const InquiryForm = ({
  type,
  fields = ["name", "email", "phone", "message"],
  submitLabel = "Send Enquiry",
  testid = "inquiry-form",
  sourcePage,
  onSuccess,
}) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitInquiry({ ...form, type, source_page: sourcePage });
      toast.success("Thank you. Our team will be in touch shortly.");
      setForm({});
      onSuccess?.();
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={testid} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6">
        {fields.map((f) => {
          const def = FIELD_DEFS[f];
          if (!def) return null;
          const isWide = def.type === "textarea";
          return (
            <div key={f} className={isWide ? "sm:col-span-2" : ""}>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">
                {def.label}{def.required && <span className="text-gold ml-1">*</span>}
              </label>
              {def.type === "textarea" ? (
                <textarea
                  data-testid={`${testid}-${f}`}
                  rows={4}
                  required={def.required}
                  value={form[f] || ""}
                  onChange={update(f)}
                  className="w-full bg-transparent border-b border-border focus:border-gold outline-none py-3 text-sm resize-none transition-colors"
                />
              ) : (
                <input
                  data-testid={`${testid}-${f}`}
                  type={def.type}
                  required={def.required}
                  value={form[f] || ""}
                  onChange={update(f)}
                  className="w-full bg-transparent border-b border-border focus:border-gold outline-none py-3 text-sm transition-colors"
                />
              )}
            </div>
          );
        })}
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid={`${testid}-submit`}
        className="group inline-flex items-center gap-3 bg-charcoal-900 text-white dark:bg-white dark:text-charcoal-900 px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-gold hover:text-charcoal-900 disabled:opacity-50 transition-colors"
      >
        {loading ? "Sending…" : submitLabel}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
};
